import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoginForm from './components/LoginForm';
import SecurityIndicators from './components/SecurityIndicators';
import PlatformBenefits from './components/PlatformBenefits';
import SocialProof from './components/SocialProof';
import Icon from '../../components/AppIcon';

const UserLogin = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    // CRITICAL FIX: Use AuthContext instead of localStorage for auth state
    // Wait for auth loading to complete before checking user state
    if (!loading && user) {
      navigate('/user-dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
                  <Icon name="BookOpen" size={20} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-primary">
                    Secure Login Portal
                  </span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                  Welcome Back
                </h1>
                <p className="text-lg text-muted-foreground">
                  Sign in to continue your book creation journey and access your AI-powered publishing tools
                </p>
              </div>

              <div className="flex justify-center lg:justify-start">
                <LoginForm />
              </div>

              <div className="hidden lg:block">
                <SecurityIndicators />
              </div>
            </div>

            <div className="space-y-8">
              <PlatformBenefits />
              
              <div className="lg:hidden">
                <SecurityIndicators />
              </div>

              <SocialProof />
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="space-y-2">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto">
                  <Icon name="Zap" size={24} color="var(--color-primary)" />
                </div>
                <h3 className="text-sm font-medium text-foreground">
                  Instant Access
                </h3>
                <p className="text-xs text-muted-foreground">
                  Start creating immediately after login with all your projects synced
                </p>
              </div>

              <div className="space-y-2">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto">
                  <Icon name="Cloud" size={24} color="var(--color-secondary)" />
                </div>
                <h3 className="text-sm font-medium text-foreground">
                  Cloud Saved
                </h3>
                <p className="text-xs text-muted-foreground">
                  All your work is automatically saved and accessible from any device
                </p>
              </div>

              <div className="space-y-2">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto">
                  <Icon name="Users" size={24} color="var(--color-accent)" />
                </div>
                <h3 className="text-sm font-medium text-foreground">
                  Community Ready
                </h3>
                <p className="text-xs text-muted-foreground">
                  Connect with fellow authors in our exclusive community
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer className="py-8 px-4 border-t border-border bg-card">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <Icon name="BookOpen" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">
                AuthorPreneur AI
              </span>
            </div>

            <div className="flex items-center space-x-6 text-xs text-muted-foreground">
              <button className="hover:text-foreground transition-colors duration-200">
                Privacy Policy
              </button>
              <button className="hover:text-foreground transition-colors duration-200">
                Terms of Service
              </button>
              <button className="hover:text-foreground transition-colors duration-200">
                Support
              </button>
            </div>

            <p className="text-xs text-muted-foreground">
              © {new Date()?.getFullYear()} AuthorPreneur AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserLogin;